import { Variants } from '@/types/common';
import type { PhotosetShareResult } from './services/shareApi';

/**
 * What one share email send came to, in the words the Share modal shows
 * (DV-3715). Shared by every send: Internal Share, Make Visible & Notify
 * (DV-3563) and the gallery's Send To (DV-3716), which differ only in their
 * `SendCopy` and in what a recipient is (a user id or an address).
 */

/** One recipient as the request names them, and how to name them to the sender. */
export interface SendRecipient {
  id: string;
  label: string;
}

export type SendTone = 'success' | 'warning' | 'error';

/** The toast variant each tone is shown in, by every send. */
export const SEND_TOAST_VARIANTS: Record<SendTone, Variants.SUCCESS | Variants.WARNING | Variants.ERROR> = {
  success: Variants.SUCCESS,
  warning: Variants.WARNING,
  error: Variants.ERROR
};

/**
 * Why a send failed as a whole, before any per-recipient answer:
 *  - `invalid` (400): the request was refused, nobody emailed.
 *  - `throttled` (429): the sender's hourly allowance is spent, nobody emailed.
 *  - `unavailable` (503): sharing could not run, nobody emailed.
 *  - `refused` (any other 4xx): nobody emailed.
 *  - `unconfirmed` (502, any other 5xx, or no answer): some emails may have
 *    gone out, so the send is never repeated automatically.
 */
export type SendFailure = 'invalid' | 'throttled' | 'unavailable' | 'refused' | 'unconfirmed';

export interface SendOutcome {
  tone: SendTone;
  /** The line shown in the modal. */
  inline: string;
  /** The toast. */
  toast: string;
  /** Recipients to leave selected: everyone certainly not sent, so the
   *  sender can send again on purpose. Anyone who may have the email
   *  (UNCONFIRMED, or everyone after a 502 or no answer) is left unchecked:
   *  asset-service says not to offer a resend to them, so sending again is a
   *  deliberate re-check, never the default. */
  keepSelected: string[];
  /** Recipients the email service throttled (RATE_LIMITED): certainly not
   *  sent, and a later send can reach them. A subset of `keepSelected`. */
  rateLimited: string[];
  /** Every recipient was sent, so the form starts over. */
  allSent: boolean;
  /** Some emails may have gone out unconfirmed (an UNCONFIRMED recipient, or
   *  a send that failed with no answer), so sending again may duplicate them. */
  unconfirmed: boolean;
  /** Set when the send failed as a whole; null when the server answered
   *  with an outcome per recipient. */
  failure: SendFailure | null;
}

/** The words that differ between the sends. */
export interface SendCopy {
  sentLine: (who: string) => string;
  /** Everyone was sent: `who` names them, `count` counts them. */
  sentToast: (who: string, count: number) => string;
  /** A 400: what the sender should check before sending again. */
  invalid: string;
  /** A 429. */
  throttled: string;
  /** A 503. */
  unavailable: string;
}

export const SEND_FAILURE_COPY = {
  unavailable: 'Couldn’t send right now. Try again.',
  refused: 'Couldn’t share this Photoset.',
  unconfirmed: 'We couldn’t confirm the emails were sent. Sending again may send duplicates.'
} as const;

/** "A", "A and B", or "A and 3 others". */
export const nameList = (labels: readonly string[]): string => {
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels[0]} and ${labels.length - 1} others`;
};

/** asset-service keys recipients trimmed and lower-cased, and answers with
 *  them as sent but trimmed: match the same way. */
const recipientKey = (id: string) => id.trim().toLowerCase();

/**
 * The outcome of a send the server answered (200), by recipient. A recipient
 * the answer does not mention cannot be called sent, so it counts as
 * UNCONFIRMED, as asset-service counts it.
 */
export const sendOutcome = (
  recipients: readonly SendRecipient[],
  result: PhotosetShareResult,
  copy: SendCopy
): SendOutcome => {
  const sentKeys = new Set(result.sent.map(recipientKey));
  const outcomeByKey = new Map(
    result.notSent.map((entry) => [recipientKey(entry.recipient), entry.outcome] as const)
  );
  const sent = recipients.filter((r) => sentKeys.has(recipientKey(r.id)));
  const unsent = recipients.filter((r) => !sentKeys.has(recipientKey(r.id)));

  if (unsent.length === 0) {
    const who = nameList(sent.map((r) => r.label));
    return {
      tone: 'success',
      inline: copy.sentLine(who),
      toast: copy.sentToast(who, sent.length),
      keepSelected: [],
      rateLimited: [],
      allSent: true,
      unconfirmed: false,
      failure: null
    };
  }

  const outcomeOf = (r: SendRecipient) => outcomeByKey.get(recipientKey(r.id)) ?? 'UNCONFIRMED';
  const labelsWith = (test: (outcome: string) => boolean) =>
    unsent.filter((r) => test(outcomeOf(r))).map((r) => r.label);
  const notSent = labelsWith((o) => o !== 'RATE_LIMITED' && o !== 'UNCONFIRMED');
  const rateLimited = labelsWith((o) => o === 'RATE_LIMITED');
  const unconfirmed = labelsWith((o) => o === 'UNCONFIRMED');

  const emails = recipients.length === 1 ? 'email' : 'emails';
  // An email that may have gone out is neither counted as sent nor called
  // unsent, so the count says what it counts.
  const confirmed = unconfirmed.length > 0 ? ' confirmed' : '';
  const shortfall = `${sent.length} of ${recipients.length} ${emails}${confirmed} sent`;
  const sentences = [`${shortfall}.`];
  if (notSent.length > 0) sentences.push(`Not sent to ${nameList(notSent)}.`);
  if (rateLimited.length > 0) {
    sentences.push(`Not sent to ${nameList(rateLimited)} yet; try again in a minute.`);
  }
  if (unconfirmed.length > 0) {
    sentences.push(
      `Couldn’t confirm the email to ${nameList(unconfirmed)}; sending again may send a duplicate.`
    );
  }

  return {
    // An error only when nobody can have been emailed.
    tone: sent.length > 0 || unconfirmed.length > 0 ? 'warning' : 'error',
    inline: sentences.join(' '),
    toast: shortfall,
    keepSelected: unsent.filter((r) => outcomeOf(r) !== 'UNCONFIRMED').map((r) => r.id),
    rateLimited: unsent.filter((r) => outcomeOf(r) === 'RATE_LIMITED').map((r) => r.id),
    allSent: false,
    unconfirmed: unconfirmed.length > 0,
    failure: null
  };
};

/** The outcome of a send that failed as a whole. Everyone stays selected,
 *  unless the emails may have gone out. */
export const sendFailureOutcome = (
  recipients: readonly SendRecipient[],
  error: unknown,
  copy: SendCopy
): SendOutcome => {
  const failure = sendFailure(error);
  const message = {
    invalid: copy.invalid,
    throttled: copy.throttled,
    unavailable: copy.unavailable,
    refused: conflictReason(error) ?? SEND_FAILURE_COPY.refused,
    unconfirmed: SEND_FAILURE_COPY.unconfirmed
  }[failure];
  return {
    tone: failure === 'unconfirmed' ? 'warning' : 'error',
    inline: message,
    toast: message,
    keepSelected: failure === 'unconfirmed' ? [] : recipients.map((r) => r.id),
    rateLimited: [],
    allSent: false,
    unconfirmed: failure === 'unconfirmed',
    failure
  };
};

/**
 * asset-service's own sentence for a 409 (its `WebError.message`), shown in
 * place of the generic line: each names something a resend cannot change (no
 * address on file, the email switched off, no name on the sender's profile).
 * Shown, never branched on. Other 4xx stay generic: a 403's body is a code,
 * not a sentence.
 */
const conflictReason = (error: unknown): string | null => {
  const { status, data } = (error ?? {}) as {
    status?: unknown;
    data?: { message?: unknown } | null;
  };
  const message = status === 409 ? data?.message : undefined;
  return typeof message === 'string' && message.trim() !== '' ? message.trim() : null;
};

/**
 * The status codes are the contract (handoff §3), never the message text.
 * Anything that is not a 4xx or a 503 — a 502, another 5xx, a network error,
 * a timeout, an unreadable 200 — may have sent some emails. An answer whose
 * body is not JSON (a WAF's or a gateway's page) is judged by its status,
 * which RTK leaves on `originalStatus`: a WAF's 403 never reached
 * asset-service, so nobody was emailed.
 */
const sendFailure = (error: unknown): SendFailure => {
  const { status: reported, originalStatus } = (error ?? {}) as {
    status?: unknown;
    originalStatus?: unknown;
  };
  const status =
    reported === 'PARSING_ERROR' && typeof originalStatus === 'number' ? originalStatus : reported;
  if (status === 400) return 'invalid';
  if (status === 429) return 'throttled';
  if (status === 503) return 'unavailable';
  if (typeof status === 'number' && status >= 400 && status < 500) return 'refused';
  return 'unconfirmed';
};
